/**
 * The files attached to one section of an employee profile.
 *
 * ── One component, three pages ────────────────────────────────────────────────────────────
 *
 * The onboarding form (where the employee attaches their Aadhaar card while typing the number),
 * Settings → Employee Information (where HR reads it, and may attach one themselves), and My
 * Information (where the employee reads their own back). The rules about what may be attached
 * and who may open it are the same on all three, and a second copy of this would be a second
 * place for them to drift — which for an identity document means one page eventually showing a
 * file another page will not.
 *
 * Read-only is the DEFAULT, not a mode: without `onChange` there is no file input and no Remove,
 * so a page that forgets to pass one cannot accidentally become editable.
 *
 * ── Why nothing is fetched on mount ───────────────────────────────────────────────────────
 *
 * Every view URL is a signed round trip through the Lambda, and a profile with four sections of
 * documents would fire a dozen of them to render a list nobody has clicked. A URL is minted when
 * somebody asks to see a file, and never stored — it expires, so a cached one works until it
 * silently does not.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  DOCUMENT_SECTION_TYPES,
  DocumentScope,
  MAX_DOCUMENT_SIZE_MB,
  documentFileName,
  getEmployeeDocumentUrl,
  uploadEmployeeDocument,
  validateDocumentFile,
} from '../../services/employeeDocuments';
import './DocumentUploads.css';

export interface DocumentUploadsProps {
  /** Whose documents, and how this page is authenticated. */
  scope: DocumentScope;
  /** Which part of the profile these belong to — also the folder they are stored in. */
  section: string;
  /** The stored keys, as held on the profile. */
  keys?: string[];
  /** Absent means read-only. Passing it is what turns on the file input and Remove. */
  onChange?: (keys: string[]) => void;
  /** Overrides the default heading, for a section that names its documents something specific. */
  label?: string;
  hint?: string;
  disabled?: boolean;
  /** Marks the heading with a *. Display only — the rule itself lives in the profile validators. */
  required?: boolean;
}

const DocumentUploads: React.FC<DocumentUploadsProps> = ({
  scope,
  section,
  keys,
  onChange,
  label = 'Documents',
  hint,
  disabled = false,
  required = false,
}) => {
  /*
   * Memoised, because `keys || []` builds a NEW empty array on every render when the section has
   * no documents yet — which is the common case — and that array is a dependency of both
   * callbacks below. Without this they are rebuilt on every keystroke anywhere in the form.
   */
  const list = useMemo(() => keys || [], [keys]);
  const editable = typeof onChange === 'function';
  const inputRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  /** The file currently going up, so a slow upload says which one it is waiting on. */
  const [uploading, setUploading] = useState('');
  /** Keys whose "View" is mid-flight, so two clicks do not open two tabs. */
  const [opening, setOpening] = useState<string[]>([]);

  const allowed = DOCUMENT_SECTION_TYPES[section] || [];

  /**
   * Files go up one at a time, deliberately.
   *
   * Not caution: picking six scans and firing six presign calls at once means six Lambda
   * invocations each doing a DynamoDB read to resolve the same token, for no gain the employee
   * can perceive on files this size. Sequential also means the first failure stops the run with
   * the rest of the selection untouched, rather than leaving a scattered half of it uploaded.
   */
  const addFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0 || !onChange) return;
      setError('');
      setBusy(true);

      const added: string[] = [];
      try {
        for (const file of Array.from(files)) {
          const problem = validateDocumentFile(section, file);
          if (problem) throw new Error(`"${file.name}": ${problem}`);
          setUploading(file.name);
          const { key } = await uploadEmployeeDocument(scope, section, file);
          added.push(key);
        }
      } catch (err: any) {
        setError(err?.message || 'That file could not be uploaded.');
      } finally {
        setUploading('');
        setBusy(false);
        /* Whatever DID upload is kept. Discarding them on a later failure would throw away
           files that are already in storage and leave the employee to upload them again. */
        if (added.length > 0) onChange([...list, ...added]);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [list, onChange, scope, section]
  );

  const open = useCallback(
    async (key: string) => {
      setError('');
      setOpening((current) => [...current, key]);
      try {
        const url = await getEmployeeDocumentUrl(scope, key);
        /* Opened rather than navigated to, because the employee is in the middle of a long form
           and losing it to a PDF viewer would lose everything they had typed. */
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err: any) {
        setError(err?.message || 'That document could not be opened.');
      } finally {
        setOpening((current) => current.filter((k) => k !== key));
      }
    },
    [scope]
  );

  /**
   * Remove takes the key off the profile; the object stays in S3.
   *
   * There is no delete permission in the Lambda's policy and that is on purpose — these are
   * statutory documents, and an onboarding form reached from an emailed link is the last place
   * that should be able to destroy one. Detaching is reversible by HR; deleting is not.
   */
  const remove = useCallback(
    (key: string) => {
      if (!onChange) return;
      onChange(list.filter((k) => k !== key));
    },
    [list, onChange]
  );

  return (
    <div className="edocs">
      <div className="edocs__head">
        <span className="edocs__label">
          {label}
          {required && <span className="edocs__req"> *</span>}
        </span>
        {editable && (
          <button
            type="button"
            className="edocs__add"
            onClick={() => inputRef.current?.click()}
            disabled={busy || disabled}
          >
            {busy ? 'Uploading…' : 'Attach file'}
          </button>
        )}
      </div>

      {hint && <p className="edocs__hint">{hint}</p>}
      {editable && allowed.length > 0 && (
        <p className="edocs__hint">
          {allowed.join(', ').toUpperCase()} · up to {MAX_DOCUMENT_SIZE_MB} MB each
        </p>
      )}

      {editable && (
        <input
          ref={inputRef}
          type="file"
          multiple
          className="edocs__input"
          aria-label={`Attach a file to ${label}`}
          accept={allowed.map((extension) => `.${extension}`).join(',')}
          onChange={(event) => addFiles(event.target.files)}
        />
      )}

      {uploading && <p className="edocs__busy">Uploading “{uploading}”…</p>}
      {error && <p className="edocs__error">{error}</p>}

      {list.length === 0 ? (
        <p className="edocs__empty">
          {editable ? 'Nothing attached yet.' : 'No documents submitted.'}
        </p>
      ) : (
        <ul className="edocs__list">
          {list.map((key) => (
            <li className="edocs__item" key={key}>
              <span className="edocs__name" title={documentFileName(key)}>
                {documentFileName(key)}
              </span>
              <button
                type="button"
                className="edocs__action"
                onClick={() => open(key)}
                disabled={opening.includes(key)}
              >
                {opening.includes(key) ? 'Opening…' : 'View'}
              </button>
              {editable && (
                <button
                  type="button"
                  className="edocs__action edocs__action--remove"
                  onClick={() => remove(key)}
                  disabled={disabled}
                  aria-label={`Remove ${documentFileName(key)}`}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DocumentUploads;
